
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useCallback, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { MapControls, Float, OrthographicCamera } from '@react-three/drei';
import * as THREE from 'three';
import { Grid, BuildingType, ZoneType } from '../types';
import { BUILDINGS } from '../constants';
import { gridToWorld } from '../utils/mapUtils';
import { UndergroundLayer, LayerVisibility } from '../types/underground';
import {
  getUndergroundConfig,
  getUndergroundDemoPlacements,
} from '../constants/undergroundDemoPlacements';

import ProceduralBuilding from './world/ProceduralBuilding';
import UndergroundInfrastructure from './world/UndergroundInfrastructure';
import { PacketSystem, AgentSystem, ZoneOverlay, CarSystem } from './world/TrafficSystems';
import { GroundTile } from './world/GroundTile';

// Helper to draw a square border using 4 thin boxes (Guaranteed no hook errors)
const TileBorder = ({ color, thickness = 0.05 }: { color: string, thickness?: number }) => (
  <group>
    <mesh position={[0, 0, -0.5 + thickness/2]}>
        <boxGeometry args={[1, thickness/2, thickness]} />
        <meshBasicMaterial color={color} />
    </mesh>
    <mesh position={[0, 0, 0.5 - thickness/2]}>
        <boxGeometry args={[1, thickness/2, thickness]} />
        <meshBasicMaterial color={color} />
    </mesh>
    <mesh position={[-0.5 + thickness/2, 0, 0]}>
        <boxGeometry args={[thickness, thickness/2, 1]} />
        <meshBasicMaterial color={color} />
    </mesh>
    <mesh position={[0.5 - thickness/2, 0, 0]}>
        <boxGeometry args={[thickness, thickness/2, 1]} />
        <meshBasicMaterial color={color} />
    </mesh>
  </group>
);

// Selection Cursor
const Cursor: React.FC<{ x: number; y: number; color: string }> = ({ x, y, color }) => {
  const [wx, _, wz] = gridToWorld(x, y);
  return (
    <group position={[wx, 0.1, wz]}>
        <mesh rotation={[-Math.PI/2,0,0]}>
            <planeGeometry args={[0.9, 0.9]} />
            <meshBasicMaterial color={color} transparent opacity={0.3} />
        </mesh>
        <TileBorder color={color} thickness={0.05} />
    </group>
  );
};

// Violation Overlay
const ViolationIndicator: React.FC<{ x: number; y: number }> = ({ x, y }) => {
    const [wx, _, wz] = gridToWorld(x, y);
    return (
        <group position={[wx, 0.12, wz]}>
            <mesh rotation={[-Math.PI/2,0,0]}>
                <planeGeometry args={[0.9, 0.9]} />
                <meshBasicMaterial color="#ef4444" transparent opacity={0.5} />
            </mesh>
            <TileBorder color="#ef4444" thickness={0.08} />
        </group>
    );
};

interface IsoMapProps {
  grid: Grid;
  onTileClick: (x: number, y: number) => void;
  hoveredTool: BuildingType;
  selectedTile: {x: number, y: number} | null;
  onHoverTileChange?: (tile: { x: number; y: number } | null) => void;
  violationTiles?: {x: number, y: number}[];
  level?: number;
  paused?: boolean;
  activeLayer?: UndergroundLayer;
  layerVisibility?: LayerVisibility;
  getPlacementError?: (x: number, y: number, tool: BuildingType) => string | null;
}

const IsoMap: React.FC<IsoMapProps> = ({
  grid,
  onTileClick,
  hoveredTool,
  selectedTile,
  onHoverTileChange,
  violationTiles = [],
  paused = false,
  activeLayer = UndergroundLayer.Surface,
  layerVisibility,
  getPlacementError,
}) => {
  const [hoveredTile, setHoveredTile] = useState<{x: number, y: number} | null>(null);
  const handleHover = useCallback((x: number, y: number) => {
    const next = { x, y };
    setHoveredTile(next);
    onHoverTileChange?.(next);
  }, [onHoverTileChange]);
  const handleLeave = useCallback(() => {
    setHoveredTile(null);
    onHoverTileChange?.(null);
  }, [onHoverTileChange]);
  const showPreview = hoveredTile && grid[hoveredTile.y][hoveredTile.x].buildingType === BuildingType.None && hoveredTool !== BuildingType.None;
  const placementError = showPreview && hoveredTile && getPlacementError
    ? getPlacementError(hoveredTile.x, hoveredTile.y, hoveredTool)
    : null;
  const previewValid = showPreview && !placementError;
  const previewPos = hoveredTile ? gridToWorld(hoveredTile.x, hoveredTile.y) : [0,0,0];

  const surfaceOpacity = layerVisibility?.transparency.surface ?? 1;
  const infraOpacity = layerVisibility?.transparency.underground1 ?? 0;
  const connectivityOpacity = layerVisibility?.transparency.underground2 ?? 0;
  const showSurface = surfaceOpacity > 0.02;
  const showTraffic = surfaceOpacity > 0.25;

  const skyColor = useMemo(() => {
    if (activeLayer === UndergroundLayer.Infrastructure) return '#475569';
    if (activeLayer === UndergroundLayer.Connectivity) return '#1e3a5f';
    return '#bae6fd';
  }, [activeLayer]);

  const sunIntensity = activeLayer === UndergroundLayer.Surface ? 1.2 : 0.7;
  const ambientIntensity = activeLayer === UndergroundLayer.Surface ? 0.8 : 1.1;

  const infraPlacements = getUndergroundDemoPlacements(UndergroundLayer.Infrastructure);
  const connectivityPlacements = getUndergroundDemoPlacements(UndergroundLayer.Connectivity);

  const renderUndergroundLayer = (
    layer: UndergroundLayer.Infrastructure | UndergroundLayer.Connectivity,
    placements: ReturnType<typeof getUndergroundDemoPlacements>,
    opacity: number
  ) => {
    if (opacity <= 0.02) return null;

    return placements.map((placement) => {
      const [wx, , wz] = gridToWorld(placement.x, placement.y);
      const config = getUndergroundConfig(placement.type);
      return (
        <UndergroundInfrastructure
          key={`ug-${layer}-${placement.x}-${placement.y}-${placement.type}`}
          position={[wx, 0.22, wz]}
          config={config}
          level={placement.level}
          isConnected={placement.isConnected}
          layerOpacity={opacity}
          onClick={() => onTileClick(placement.x, placement.y)}
          status={placement.isConnected ? 'online' : 'degraded'}
        />
      );
    });
  };

  return (
    <div
      className="absolute inset-0 touch-none transition-colors duration-700"
      style={{ backgroundColor: skyColor }}
      data-testid="iso-map"
      data-active-layer={activeLayer}
      data-surface-opacity={surfaceOpacity.toFixed(2)}
    >
      <Canvas shadows dpr={[1, 1.5]} gl={{ antialias: true }}>
        {/* Isometric Camera Setup */}
        <OrthographicCamera makeDefault zoom={40} position={[20, 20, 20]} near={-100} far={200} />
        
        <MapControls 
          enableRotate={true} 
          enableZoom={true} 
          minZoom={20} 
          maxZoom={100} 
          maxPolarAngle={Math.PI / 2.1}
          minPolarAngle={0.1}
          target={[0,0,0]} 
        />
        
        <ambientLight intensity={ambientIntensity} color="#bae6fd" />
        <directionalLight 
            castShadow 
            position={[15, 25, 10]} 
            intensity={sunIntensity} 
            color="#fff" 
            shadow-mapSize={[1024, 1024]} 
            shadow-bias={-0.0001} 
        >
           <orthographicCamera attach="shadow-camera" args={[-20, 20, 20, -20]} />
        </directionalLight>

        <group>
          {grid.map((row, y) => row.map((tile, x) => (
              <React.Fragment key={`${x}-${y}`}>
                <GroundTile type={tile.buildingType} x={x} y={y} grid={grid} isConnected={tile.isConnected} onHover={handleHover} onLeave={handleLeave} onClick={onTileClick} />
                {showSurface && (
                <group position={[gridToWorld(x, y)[0], 0.1, gridToWorld(x, y)[2]]}>
                    {tile.buildingType !== BuildingType.None && tile.buildingType !== BuildingType.Pipeline && tile.buildingType !== BuildingType.Road && (
                      <ProceduralBuilding 
                        type={tile.buildingType} 
                        baseColor={BUILDINGS[tile.buildingType].color} 
                        x={x} y={y} 
                        isConnected={tile.isConnected}
                        level={tile.level}
                        opacity={surfaceOpacity}
                        transparent={surfaceOpacity < 1}
                      />
                    )}
                </group>
                )}
              </React.Fragment>
          )))}
          
          <ZoneOverlay grid={grid} />

          {/* Violation Overlays */}
          {violationTiles.map((t, i) => (
              <ViolationIndicator key={`viol-${t.x}-${t.y}-${i}`} x={t.x} y={t.y} />
          ))}

          {showTraffic && <PacketSystem grid={grid} paused={paused} />}
          {showTraffic && <CarSystem grid={grid} paused={paused} />}

          {/* --- Agent Systems by Zone --- */}

          {showTraffic && (
            <>
          {/* Public / DMZ (Green, no hat) */}
          <AgentSystem grid={grid} color="#4ade80" zoneTypes={[ZoneType.Public, ZoneType.DMZ]} paused={paused} />
          
          {/* Ops / Restricted (Orange, no hat) */}
          <AgentSystem grid={grid} color="#fb923c" zoneTypes={[ZoneType.Restricted, ZoneType.Ops]} paused={paused} />

          {/* Legal (Purple, no hat) */}
          <AgentSystem grid={grid} color="#a855f7" zoneTypes={[ZoneType.Legal]} paused={paused} />
          
          {/* Core (Governance/Admin) (Gold, HAT) */}
          <AgentSystem grid={grid} color="#fbbf24" zoneTypes={[ZoneType.Core]} paused={paused} /> 
          
          {/* Security (Cyan, HAT) */}
          <AgentSystem grid={grid} color="#06b6d4" zoneTypes={[ZoneType.Security]} paused={paused} />
          
          {/* JumpGate Specific (Pink, HAT) */}
          <AgentSystem grid={grid} color="#d946ef" specificType={BuildingType.JumpGate} paused={paused} />
            </>
          )}

          {showPreview && hoveredTile && (
            <group position={[previewPos[0], 0.2, previewPos[2]]}>
              {placementError ? (
                <TileBorder color="#ef4444" thickness={0.08} />
              ) : (
                <Float speed={5} rotationIntensity={0} floatIntensity={0.2} floatingRange={[0, 0.2]}>
                  <ProceduralBuilding
                    type={hoveredTool}
                    baseColor={previewValid ? BUILDINGS[hoveredTool].color : '#ef4444'}
                    x={hoveredTile.x}
                    y={hoveredTile.y}
                    transparent
                    opacity={previewValid ? 0.6 : 0.35}
                  />
                </Float>
              )}
            </group>
          )}

          {renderUndergroundLayer(UndergroundLayer.Infrastructure, infraPlacements, infraOpacity)}
          {renderUndergroundLayer(UndergroundLayer.Connectivity, connectivityPlacements, connectivityOpacity)}

          {selectedTile && <Cursor x={selectedTile.x} y={selectedTile.y} color="#fbbf24" />}
          {hoveredTile && <Cursor x={hoveredTile.x} y={hoveredTile.y} color={hoveredTool === BuildingType.None ? '#ef4444' : '#38bdf8'} />}
        </group>
      </Canvas>
    </div>
  );
};

export default IsoMap;
